import type { MouseEvent } from 'react';
import React, { useState, useRef } from 'react';
import type { Theme } from '@material-ui/core';
import {
    Divider,
    ListItem,
    ListItemText,
    ListSubheader,
    Typography,
} from '@material-ui/core';
import { useDeleteLeverandorProcessMutation } from '../../services/rtkApi';
import { useTranslation } from 'react-i18next';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Block } from '@klp/design';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { ConfirmationDialog } from '@klp/design/ConfirmationDialog';
import { useHistory } from 'react-router';
import { LeverandorRoutes } from '@klp/leverandor/config';
import { showToast } from '@klpkonsern/ui';

export type MenuPopperDeleteLeverandorProps = {
    canDelete: boolean;
    prosessInstansId: string | undefined;
    navigateOnDelete: boolean;
    onClose?: () => void;
};

const styles = (theme: Theme) => ({
    redText: {
        color: 'red',
    },

    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
    },
});
export const MenuPopperDeleteLeverandor = ({
    canDelete,
    prosessInstansId,
    navigateOnDelete,
    onClose,
}: MenuPopperDeleteLeverandorProps) => {
    const classes = useKlpStyles({}, styles);
    const { t } = useTranslation();
    const history = useHistory();
    const anchorRef = useRef(null);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [deleteProsjekt, { isLoading }] =
        useDeleteLeverandorProcessMutation();

    const handleDeleteProsjekt = async (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setShowConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!prosessInstansId) return;

        await deleteProsjekt({ prosessInstansId })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('leverandor.successfulOperation'),
                    type: 'success',
                });

                if (navigateOnDelete) {
                    history.push(LeverandorRoutes.leverandorSchema());
                } else {
                    setShowConfirmDialog(false);
                    onClose?.();
                }
            })
            .catch(() => {
                setShowConfirmDialog(false);
                onClose?.();
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
    };

    if (!canDelete) return null;

    return (
        <>
            <Divider />
            <ListSubheader disableSticky={true}>
                {t('general.delete')}
            </ListSubheader>
            <ListItem
                dense={true}
                button={true}
                onClick={handleDeleteProsjekt}
                disabled={isLoading}
                ref={anchorRef}
            >
                <Block className={classes.iconContainer}>
                    <Illustration icon={IconName.DELETE} />
                </Block>
                <ListItemText>
                    <Typography className={classes.redText}>
                        {t('leverandor.deleteLeverandor.title')}
                    </Typography>
                </ListItemText>
            </ListItem>

            <ConfirmationDialog
                anchorRef={anchorRef}
                showDialog={showConfirmDialog}
                dialogContent={t(
                    'leverandor.deleteLeverandor.confirmationText',
                )}
                confirmText={t('general.deleteYes')}
                cancelText={t('general.cancel')}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isLoading={isLoading}
                className="withBlur"
            />
        </>
    );
};
